import {
  useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notifications } from '@mantine/notifications';
import { ErrorFallback } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils';
import { deepClone, getProgressStepper } from 'src/utils/helpers';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { ReactComponent as IconSuccessGreen } from "src/assets/icons/success-green.svg"
import { Checkbox, Flex } from '@mantine/core'
import useCleverTapEvents from 'src/events/useGetEvents';
import { ESIGN_EVENT } from 'src/events/constant';
import { RootState } from '../../redux/store';
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { useStyles } from './aadhaarEsignForm.styles';
import { SignPageModal } from './modals/alertModal';
import { PrimaryAlertModal } from './modals/primaryAlertModal';
import {
  clearDigioFormError, getAadhaarEsignCredentials, setDigioFormStatus,
  verifyAadhaarEsign,
  ESignDataResponse,
  DigioDocumentCallback,
} from './digioFormSlice';
import { initDigio } from './initDigio';
import { ScreenLoader } from '../../components/screenLoader';
import { Button } from '../../components/atoms';

export const AadhaarEsignForm = () => {
  const { classes, cx } = useStyles();
  const { TriggerEvent } = useCleverTapEvents();
  const [isMobileNotConnectedModalOpen, setIsMobileNotConnectedModalOpen] = useState(false);
  const [isFnoModalOpen, setIsFnoModalOpen] = useState(false);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.account.userDetails);
  const [currentForm, setCurrentForm] = useState<null | ESignDataResponse>(null);
  const [forms, setForms] = useState<ESignDataResponse[]>([]);

  const digioState = useSelector((state: RootState) => state.digioData);

  const getCurrentForm = () => forms.find((element) => element.signed === false);

  useEffect(() => {
    dispatch(setDigioFormStatus('loading'));
    const timer = setTimeout(() => {
      dispatch(getAadhaarEsignCredentials());
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (digioState.status === 'success') {
      const { eSignData = [] } = digioState;
      if (digioState.isEsignVerificationStarted && eSignData) {
        setForms(deepClone(eSignData));
        dispatch(setDigioFormStatus('idle'));
      }
      dispatch(setDigioFormStatus('idle'));
    } else if (digioState.status === 'error') {
      dispatch(clearDigioFormError());
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(digioState.error.message) || ErrorFallback,
        icon: <IconError />,
      });
      TriggerEvent(ESIGN_EVENT.esignError, { errorMessage: digioState.error.message || "" });
    }
  }, [digioState.status]);

  useEffect(() => {
    const currForm = getCurrentForm();
    if (currForm) {
      setCurrentForm(currForm);
    }
  }, [forms])

  const handleSign = () => {
    if (currentForm) {
      const { identifier, eSignRequestID, token } = currentForm
      initDigio({
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        callback: digioCallback,
        identifier,
        requestId: eSignRequestID,
        token,
      });
      if (currentForm.name === "CVL KRA form") {
        TriggerEvent(ESIGN_EVENT.kra, { name: userDetails.name });
      } else if (currentForm.name === "KYC documents") {
        TriggerEvent(ESIGN_EVENT.kyc, { name: userDetails.name })
      }
    } else {
      dispatch(getAadhaarEsignCredentials());
    }
  };

  const handleSetModalOpen = () => {
    setIsMobileNotConnectedModalOpen(true);
  };

  const digioCallback = (response: DigioDocumentCallback) => {
    if (!response) {
      notifications.show({
        color: 'red',
        message: 'Something went wrong, Try again!',
        icon: <IconError />,
      });
      return
    }
    if (response.error_code) {
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(response?.message) || ErrorFallback,
        icon: <IconError />,
      });
      TriggerEvent(ESIGN_EVENT.esignError, { errorMessage: response?.message });
      return
    }
    notifications.show({
      color: 'green',
      message: `Esign for ${currentForm?.name} successfully!`,
      icon: <IconSuccess />,
    });
    const signedDoc = response.digio_doc_id;
    const idx = forms.findIndex((form) => form.eSignRequestID === signedDoc);
    const modForms = [...forms];

    modForms[idx].signed = true;
    setForms(modForms);

    const pendingForms = forms.filter((form) => form.signed === false);
    const form = getCurrentForm();
    if (pendingForms.length && form) { // if documents are still left for signing
      if (form) {
        setCurrentForm(form);
      }
    } else {
      dispatch(verifyAadhaarEsign({}));
      TriggerEvent(ESIGN_EVENT.esignComplete, {});
    }
  };

  const checkBoxOptions = (label: string, isSigned: boolean) => (
    <Checkbox
      classNames={{
        body: classes.checkBoxBody,
        label: classes.checkBoxLabel,
        labelWrapper: classes.labelWrapper,
      }}
      labelPosition="right"
      label={(
        <div className={classes.innerLabelWrapper}>
          <p>{label}</p>
          {isSigned ? <IconSuccessGreen /> : null}
        </div>
      )}
      // if it is filled already
      disabled={label !== currentForm?.name}
      // if this is the current form
      checked={label === currentForm?.name || isSigned}
    />
  );

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(userDetails.currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">Aadhaar eSign</h2>
          <p className="csDescription">
            You will receive an OTP on the number linked to your Aadhaar card.
          </p>
          <Flex className={classes.optionsWrapper}>
            {forms.map((form) => (
              <button
                type="button"
                key={form.eSignRequestID}
                className={cx(
                  classes.buttonWBox,
                  currentForm?.name === form.name ? classes.buttonSelectedAdditionalStyles : null
                )}
              >
                {checkBoxOptions(form.name, form.signed)}
              </button>
            ))}
          </Flex>
          <button
            type="button"
            className={classes.adhaarSecCtaLink}
            onClick={handleSetModalOpen}
          >
            Aadhaar is not linked to this mobile no.?
          </button>
        </div>
        <div className={cx("csFooter", classes.footer)}>
          <Button disabled={false} onClick={handleSign}>
            { digioState.eSignData?.length ? 'Sign' : 'Retry'}
          </Button>
        </div>
        <PrimaryAlertModal
          opened={isMobileNotConnectedModalOpen}
          onClose={() => {
            setIsMobileNotConnectedModalOpen(false);
          }}
          title="Don’t worry we got you!"
          description={`You can either link it through the official Aadhaar update process or opt for an 
offline e-sign method. For the offline method, please email us at support@tiqs.in, and we'll send you the 
necessary form to fill out, sign, and return to us.`}
          sBtnText="Close"
          onSecondaryBtnClick={() => {
            setIsMobileNotConnectedModalOpen(false);
          }}
        />
        <SignPageModal
          opened={isFnoModalOpen}
          onClose={() => {
            setIsFnoModalOpen(false);
          }}
          title="Do you want to enable FnO?"
          description="Future and options can be enabled by just 1 more step."
          pBtnText="Enable FnO"
          sBtnText="Remind me Later!"
          onPrimaryBtnClick={() => { }}
          onSecondaryBtnClick={() => {
            setIsFnoModalOpen(false);
          }}
        />
      </div>
      {digioState.status === "loading" ? <ScreenLoader /> : null}
    </ScreenWrapper>
  );
};

export default AadhaarEsignForm;
